import {
  ConfirmationDialog,
  NewFormSection as FormSection,
  useDialogs,
  FormSectionTitle,
  SettingsPage
} from '@paintscout/ui'
import type { ValueType } from '@paintscout/ui'
import type { EventTypeOption } from '@paintscout/util/builder'
import {
  copyEventTypes,
  createEventType,
  deleteEventType,
  deleteEventTypes,
  getEventTypes,
  getObjectLabels,
  reorderEventTypes
} from '@paintscout/util/builder'
import { Form, useFormikContext } from 'formik'
import type { OptionsDocument, RatesDocument } from 'paintscout'
import React, { useEffect, useState } from 'react'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { OptionsTileList } from '../..'
import { EventTypeDialog } from '../../dialogs'

export interface EventTypeOptionsProps {
  onDialogConfirm?: () => void
  production?: boolean
}

function EventTypeOptions({ onDialogConfirm, production = false }: EventTypeOptionsProps) {
  const { openDialog, dismissDialog } = useDialogs()
  const { values, setFieldValue } = useFormikContext<{
    options: OptionsDocument
    rates: RatesDocument
  }>()
  const [selected, setSelected] = React.useState<ValueType[]>([])
  const { options } = values
  const quoteLabel = getObjectLabels({ options }).quote
  const { productionFlag } = useFlags()
  const [eventTypes, setEventTypes] = useState(getEventTypes({ options, onlyProduction: production }))
  useEffect(() => {
    setEventTypes(getEventTypes({ options, onlyProduction: production }))
  }, [options, production])

  function handleCreateItem(_: React.MouseEvent) {
    const eventType = createEventType({ production })
    openDialog(EventTypeDialog, {
      options,
      eventType,
      isNew: true,
      onConfirm: async (updatedOptions: OptionsDocument) => {
        setFieldValue('options', updatedOptions)
        dismissDialog()
        if (onDialogConfirm) {
          onDialogConfirm()
        }
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  function handleDeleteItem(event: any, key: string) {
    openDialog(ConfirmationDialog, {
      message: (
        <>
          <p>This event type will no longer be available for created events.</p>
          <p>Existing events will not be affected.</p>
        </>
      ),
      onConfirm: (_ev: React.MouseEvent<HTMLElement>) => {
        dismissDialog()
        const updatedOptions = deleteEventType({
          eventType: eventTypes.find((item) => item.key === key),
          options
        })
        setFieldValue('options', updatedOptions)
        if (onDialogConfirm) {
          onDialogConfirm()
        }
      },
      onCancel: (_ev: React.MouseEvent<HTMLElement>) => {
        dismissDialog()
      }
    })
  }

  function handleDeleteBulkItems(event: React.MouseEvent, keys: ValueType[]) {
    openDialog(ConfirmationDialog, {
      message: (
        <>
          <p>These event types will no longer be available.</p>
          <p>Existing event types will not be affected.</p>
          <p>
            Please note, <strong>this can not be undone.</strong>
          </p>
        </>
      ),
      onConfirm: (_ev: React.MouseEvent<HTMLElement>) => {
        dismissDialog()
        const selectedEventTypes = eventTypes.filter((i) => keys.includes(i.key))
        if (selectedEventTypes.length === eventTypes.length) {
          // if all event types are selected, keep the first one
          selectedEventTypes.splice(0, 1)
        }
        const updatedOptions = deleteEventTypes({
          eventTypes: selectedEventTypes,
          options
        })

        setFieldValue('options', updatedOptions)
        setSelected([])
      },
      onCancel: (_ev: React.MouseEvent<HTMLElement>) => {
        dismissDialog()
      }
    })
  }

  function handleEditItem(_event: React.MouseEvent, key: string) {
    const eventType = eventTypes.find((item) => item.key === key)

    openDialog(EventTypeDialog, {
      options,
      eventType,
      isNew: false,
      onConfirm: async (updatedOptions: OptionsDocument, isDirty?: boolean) => {
        dismissDialog()
        setFieldValue('options', updatedOptions)
        if (onDialogConfirm && isDirty) {
          onDialogConfirm()
        }
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  function handleReorder(eventTypes: EventTypeOption[]) {
    const updatedOptions = reorderEventTypes({
      eventTypes,
      options
    })
    setFieldValue('options', updatedOptions)
  }

  function handleCopyItem(event: any, key: string) {
    const updatedOptions = copyEventTypes({
      options,
      key
    })
    setFieldValue('options', updatedOptions)
  }
  return (
    <SettingsPage
      title={'Events'}
      callout={{
        path: 'settings-event-types',
        content: `Organize appointments like ${quoteLabel.plural.toLowerCase()} and follow-up calls into clear event types.${productionFlag ? ' These event types only apply to the Sales calendar.' : ''}`,
        learnMoreLink: 'https://help.paintscout.com/en/articles/12046425-event-types'
      }}
    >
      <Form>
        <FormSection>
          <FormSectionTitle title={'Sales Event Types'} variant={'h3'} />
          <OptionsTileList
            title={''}
            items={eventTypes}
            createTitle={'Add New Event Type'}
            showCreate={true}
            selected={selected}
            setSelected={setSelected}
            onDeleteBulkItems={handleDeleteBulkItems}
            onReorder={handleReorder}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            onCreateClick={handleCreateItem}
            onCopyItem={handleCopyItem}
            noItemTitle={`No ${production ? 'Production ' : ''}Event Types`}
            noItemHelpText={`Organize your ${production ? 'production ' : ''}events with specific types.`}
            allowEmpty={false}
          />
        </FormSection>
      </Form>
    </SettingsPage>
  )
}

export default EventTypeOptions
